import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

function Insulation() {
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        const timer = setTimeout(() => {
            window.scrollTo(0, 0);
            setIsLoaded(true);
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    if (!isLoaded) {
        return (
            <div className="min-h-screen bg-brand-cream flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-orange mx-auto mb-4"></div>
                    <p className="text-brand-green">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-brand-cream" style={{ opacity: 1 }}>
            {/* Hero Section */}
            <section className="bg-brand-green text-white py-20">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    <motion.h1
                        className="text-4xl md:text-6xl font-bold mb-6"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        Auckland's Trusted Insulation Experts
                    </motion.h1>
                    <motion.p
                        className="text-xl md:text-2xl max-w-3xl mx-auto"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                    >
                        High-quality, reliable insulation services for new builds, renovations, and retrofits across Auckland
                    </motion.p>
                </div>
            </section>

            {/* Introduction Section */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                            <motion.h2
                                className="text-3xl md:text-4xl font-bold text-black mb-6"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                            >
                                Welcome to Mega Property Solutions
                            </motion.h2>
                            <motion.p
                                className="text-lg text-gray-700 mb-6"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                            >
                                At Mega Property Solutions, we specialise in delivering high-quality, reliable insulation services across Auckland and the surrounding regions. Whether you're building a brand-new home, renovating an existing space, or upgrading an older property for better energy performance, our experienced team is here to help.
                            </motion.p>
                            <motion.p
                                className="text-lg text-gray-700 mb-8"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.4 }}
                            >
                                With a strong reputation for workmanship, efficiency, and professionalism, we've become a go-to partner for homeowners, builders, and developers across Auckland. We bring both expertise and flexibility to every job — whether it's a small residential retrofit or a large-scale new build.
                            </motion.p>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.5 }}
                                className="flex flex-col sm:flex-row gap-4"
                            >
                                <Link
                                    to="/contact"
                                    className="inline-block bg-brand-orange text-white px-8 py-3 rounded-lg font-semibold hover:bg-brand-orange-hover transition-colors duration-300 shadow-md hover:shadow-lg"
                                >
                                    Get Free Quote
                                </Link>
                            </motion.div>
                        </motion.div>
                        <motion.div
                            className="relative"
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                            <div className="bg-gray-200 h-96 rounded-2xl overflow-hidden shadow-lg">
                                <img
                                    src={require('../images/gallery/insulation-greenstuf.png')}
                                    alt="Professional insulation service"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Complete Insulation Services Section */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
                            Complete Insulation Services for Every Type of Project
                        </h2>
                        <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                            We offer a full range of insulation solutions to meet the diverse needs of our clients
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* New Builds */}
                        <motion.div
                            className="bg-brand-cream rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.1 }}
                        >
                            <div className="bg-brand-green rounded-xl p-4 w-16 h-16 flex items-center justify-center mb-4">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-black mb-3">New Builds</h3>
                            <p className="text-gray-700">
                                We work with builders, project managers, and architects to ensure insulation is installed to the highest standard and in full compliance with the New Zealand Building Code (NZBC). Our team integrates seamlessly into your build schedule to help you deliver warm, energy-efficient homes and commercial spaces.
                            </p>
                        </motion.div>

                        {/* Renovations & Retrofits */}
                        <motion.div
                            className="bg-brand-cream rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            <div className="bg-brand-green rounded-xl p-4 w-16 h-16 flex items-center justify-center mb-4">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-black mb-3">Renovations & Retrofits</h3>
                            <p className="text-gray-700">
                                Retrofitting insulation into older homes or buildings can significantly improve comfort, reduce energy bills, and add value. Whether it's a ceiling top-up, wall injection, or underfloor install, we can help bring your property up to modern insulation standards.
                            </p>
                        </motion.div>

                        {/* Acoustic Insulation */}
                        <motion.div
                            className="bg-brand-cream rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                        >
                            <div className="bg-brand-green rounded-xl p-4 w-16 h-16 flex items-center justify-center mb-4">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-black mb-3">Acoustic Insulation</h3>
                            <p className="text-gray-700">
                                We provide targeted soundproofing solutions to reduce noise transfer between rooms, floors, or from external sources. Ideal for multi-dwelling units, offices, and any environment where sound control is essential.
                            </p>
                        </motion.div>

                        {/* Fire-Rated Insulation */}
                        <motion.div
                            className="bg-brand-cream rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                        >
                            <div className="bg-brand-green rounded-xl p-4 w-16 h-16 flex items-center justify-center mb-4">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-black mb-3">Fire-Rated Insulation</h3>
                            <p className="text-gray-700">
                                Fire safety is critical in today's building environment. We install certified fire-resistant insulation systems designed to slow fire spread and enhance occupant safety, particularly in multi-storey buildings, apartments, and commercial properties.
                            </p>
                        </motion.div>

                        {/* Ceiling, Wall & Underfloor */}
                        <motion.div
                            className="bg-brand-cream rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.5 }}
                        >
                            <div className="bg-brand-green rounded-xl p-4 w-16 h-16 flex items-center justify-center mb-4">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-black mb-3">Ceiling, Wall & Underfloor</h3>
                            <p className="text-gray-700">
                                We cover all aspects of your building envelope, using trusted products to ensure complete, efficient thermal coverage across ceilings, walls, and underfloor areas of residential and commercial buildings.
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Insulation Products Section */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
                            Premium Insulation Products
                        </h2>
                        <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                            We work with New Zealand's leading insulation brands to deliver superior thermal and acoustic performance
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* Pink Batts */}
                        <motion.div
                            className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                        >
                            <div className="bg-gray-200 rounded-xl overflow-hidden h-48 mb-4">
                                <img
                                    src={require('../images/products/insulation-pinkbatts.jpg')}
                                    alt="Pink Batts insulation"
                                    className="w-full h-full object-contain"
                                />
                            </div>
                            <h3 className="text-xl font-bold text-black mb-3">Pink Batts Insulation</h3>
                            <p className="text-gray-700 text-sm">
                                Popular thermal and acoustic insulation made from lightweight glass wool, known for its distinctive pink color. Designed to improve energy efficiency and comfort in walls, ceilings, and underfloors.
                            </p>
                        </motion.div>

                        {/* Eliment */}
                        <motion.div
                            className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <div className="bg-gray-200 rounded-xl overflow-hidden h-48 mb-4">
                                <img
                                    src={require('../images/products/insulation-eliment.png')}
                                    alt="Eliment insulation"
                                    className="w-full h-full object-contain"
                                />
                            </div>
                            <h3 className="text-xl font-bold text-black mb-3">Eliment Insulation</h3>
                            <p className="text-gray-700 text-sm">
                                A high-performance thermal solution designed to enhance energy efficiency in residential and commercial buildings. It offers excellent heat retention and moisture resistance, making it ideal for New Zealand's diverse climate conditions.
                            </p>
                        </motion.div>

                        {/* Mammoth */}
                        <motion.div
                            className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                        >
                            <div className="bg-gray-200 rounded-xl overflow-hidden h-48 mb-4">
                                <img
                                    src={require('../images/products/insulation-mammoth.png')}
                                    alt="Mammoth insulation"
                                    className="w-full h-full object-contain"
                                />
                            </div>
                            <h3 className="text-xl font-bold text-black mb-3">Mammoth Insulation</h3>
                            <p className="text-gray-700 text-sm">
                                A New Zealand-made, polyester-based insulation known for its durability, safety, and sustainable manufacturing. Soft, non-irritating, and free from harsh chemicals, it's ideal for creating warmer, quieter, and healthier indoor environments.
                            </p>
                        </motion.div>

                        {/* GreenStuf */}
                        <motion.div
                            className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                        >
                            <div className="bg-gray-200 rounded-xl overflow-hidden h-48 mb-4">
                                <img
                                    src={require('../images/products/insulation-greenstuf.png')}
                                    alt="GreenStuf insulation"
                                    className="w-full h-full object-contain"
                                />
                            </div>
                            <h3 className="text-xl font-bold text-black mb-3">GreenStuf Insulation</h3>
                            <p className="text-gray-700 text-sm">
                                A New Zealand-made, 100% polyester insulation designed to provide long-lasting thermal and acoustic performance. It's non-toxic, non-irritating, and environmentally friendly, making it a safe choice for homes, schools, and commercial buildings.
                            </p>
                        </motion.div>

                        {/* Knauf */}
                        <motion.div
                            className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.5 }}
                        >
                            <div className="bg-gray-200 rounded-xl overflow-hidden h-48 mb-4">
                                <img
                                    src={require('../images/products/insulation-knauf.jpg')}
                                    alt="Knauf insulation"
                                    className="w-full h-full object-contain"
                                />
                            </div>
                            <h3 className="text-xl font-bold text-black mb-3">Knauf Insulation</h3>
                            <p className="text-gray-700 text-sm">
                                A durable and eco-friendly solution made from glass mineral wool, designed to deliver excellent thermal and acoustic performance. Known for its sustainability and ease of installation, it's widely used in both residential and commercial construction projects.
                            </p>
                        </motion.div>

                        {/* Expol */}
                        <motion.div
                            className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.6 }}
                        >
                            <div className="bg-gray-200 rounded-xl overflow-hidden h-48 mb-4">
                                <img
                                    src={require('../images/products/insulation-expol.png')}
                                    alt="Expol insulation"
                                    className="w-full h-full object-contain"
                                />
                            </div>
                            <h3 className="text-xl font-bold text-black mb-3">Expol Insulation</h3>
                            <p className="text-gray-700 text-sm">
                                A rigid polystyrene-based solution commonly used for underfloor and wall insulation in both new builds and retrofits. Lightweight, moisture-resistant, and durable, it helps improve energy efficiency and comfort in a wide range of building types.
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Partnerships Section */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div
                        className="text-center mb-12"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
                            Strong Partnerships with New Zealand's Leading Building Suppliers
                        </h2>
                        <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                            At Mega Property Solutions, we believe that quality insulation starts with quality materials. That's why we've built strong, long-standing partnerships with Placemakers, Mitre10, Bunnings, and ITM — some of New Zealand's most trusted building supply companies.
                        </p>
                    </motion.div>

                    <motion.p
                        className="text-lg text-gray-700 max-w-3xl mx-auto mb-12 text-center"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        These partnerships give us consistent access to a wide range of high-performance insulation products from top brands, all at competitive pricing. Whether your project requires glasswool, polyester, acoustic panels, or fire-rated solutions, we can source and install the right products to suit your needs — quickly and efficiently.
                    </motion.p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {[
                            { name: 'Placemaker', image: require('../images/partners/placemaker.jpg') },
                            { name: 'Mitre 10', image: require('../images/partners/mitre-10.webp') },
                            { name: 'Bunnings', image: require('../images/partners/bunnings.jpg') },
                            { name: 'ITM', image: require('../images/partners/itm.png') }
                        ].map((partner, index) => (
                            <motion.div
                                key={partner.name}
                                className="bg-white rounded-xl p-4 shadow-md hover:shadow-xl transition-shadow duration-300 flex items-center justify-center h-24"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.3 + (index * 0.1) }}
                                whileHover={{ scale: 1.05, y: -5 }}
                            >
                                <img
                                    src={partner.image}
                                    alt={`${partner.name} - Trusted Partner`}
                                    className="max-h-12 w-auto object-contain transition-all duration-300"
                                />
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us Section */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
                            Why Choose Mega Property Solutions?
                        </h2>
                        <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                            We pride ourselves on delivering insulation solutions that not only meet but exceed client expectations
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { text: 'Auckland-based, locally owned and operated' },
                            { text: 'Experienced and trained insulation professionals' },
                            { text: 'Fully compliant with NZ Building Code and energy efficiency requirements' },
                            { text: 'Strong supplier relationships for fast, reliable product access' },
                            { text: 'Clean, safe, and respectful work on every site' },
                            { text: 'Competitive, transparent pricing with no hidden costs' },
                            { text: 'Excellent communication from start to finish' },
                            { text: 'Comprehensive guarantees and ongoing support for all projects' }
                        ].map((item, index) => (
                            <motion.div
                                key={index}
                                className="bg-white rounded-xl p-6 shadow-lg"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                            >
                                <div className="flex items-start">
                                    <span className="text-brand-orange mr-3 mt-1 text-xl">✓</span>
                                    <p className="text-gray-700">{item.text}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <motion.p
                        className="text-lg text-gray-700 max-w-3xl mx-auto mt-12 text-center"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.8 }}
                    >
                        Our goal is to help you create warmer, quieter, safer, and more energy-efficient homes and buildings — while making the process smooth, stress-free, and cost-effective.
                    </motion.p>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 bg-brand-green text-white">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    <motion.h2
                        className="text-3xl md:text-4xl font-bold mb-6"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        Let's Get Your Project Started
                    </motion.h2>
                    <motion.p
                        className="text-xl mb-8 max-w-2xl mx-auto"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                    >
                        Whether you're a homeowner, builder, or project manager, we're here to help you make the right insulation decisions. Contact Mega Property Solutions today to discuss your project or request a free, no-obligation quote.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
                    >
                        <Link
                            to="/contact"
                            className="inline-block bg-brand-orange text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-brand-orange-hover transition-all duration-300 shadow-lg hover:shadow-xl"
                        >
                            Contact Us Today
                        </Link>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}

export default Insulation;
